import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { Close as CloseIcon, Coffee } from "@mui/icons-material";
import dayjs from "dayjs";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";
import useTokenRefresh from "../../../../Hooks/useTokenRefresh";

// Import modular components
import DateFilters from "./BreakReport/DateFilters";
import ActiveFilters from "./BreakReport/ActiveFilters";
import TimeFormatInfo from "./BreakReport/TimeFormatInfo";
import BreakLogList from "./BreakReport/BreakLogList";
import EmptyState from "./BreakReport/EmptyState";
import LoadingState from "./BreakReport/LoadingState";
import PaginationControls from "./BreakReport/PaginationControls";

const BreakReportDialog = ({ open, onClose, selectedUser }) => {
  const [breakData, setBreakData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedBreak, setExpandedBreak] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Applied filter states (actual filters being used in API)
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  // Client-side pagination for break details (per break log)
  const [detailsPageMap, setDetailsPageMap] = useState({});
  const detailsPerPage = 10;

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchBreakDetails = async () => {
    setLoading(true);
    try {
      let url = `users/${selectedUser.id}/break-details?page=${page}&pagination=${pagination}`;

      if (appliedStartDate) {
        url += `&start_date=${appliedStartDate}`;
      }
      if (appliedEndDate) {
        url += `&end_date=${appliedEndDate}`;
      }

      const res = await httpclient.get(url);
      const { data } = res;

      if (data.status === 200) {
        setBreakData(data.data);
        setTotalRecords(data.meta?.total || 0);
      } else {
        showNotification(
          "error",
          data.message || "Failed to fetch break details"
        );
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedUser?.id) {
      fetchBreakDetails();
      setExpandedBreak(null);
      setPage(1);
      // Reset filters when dialog opens
      setAppliedStartDate("");
      setAppliedEndDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser]);

  useEffect(() => {
    if (open && selectedUser?.id) {
      fetchBreakDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pagination, appliedStartDate, appliedEndDate]);

  const handleExpandClick = (breakIndex) => {
    setExpandedBreak(expandedBreak === breakIndex ? null : breakIndex);
    // Initialize pagination for this break log if not already set
    if (!detailsPageMap[breakIndex]) {
      setDetailsPageMap((prev) => ({ ...prev, [breakIndex]: 1 }));
    }
  };

  const handleDetailsPageChange = (breakIndex, newPage) => {
    setDetailsPageMap((prev) => ({ ...prev, [breakIndex]: newPage }));
  };

  const handleApplyFilters = (startDate, endDate) => {
    setPage(1);
    setAppliedStartDate(startDate ? dayjs(startDate).format("YYYY-MM-DD") : "");
    setAppliedEndDate(endDate ? dayjs(endDate).format("YYYY-MM-DD") : "");
  };

  const handleClearAllFilters = () => {
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPage(1);
  };

  const handleClearStartDate = () => {
    setAppliedStartDate("");
    setPage(1);
  };

  const handleClearEndDate = () => {
    setAppliedEndDate("");
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
    setPage(1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[10],
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Coffee color="primary" />
          <Typography variant="h6" fontWeight="700" color="text.primary">
            Break Report
          </Typography>
          {selectedUser?.full_name && (
            <Typography variant="body2" fontWeight="500" color="text.secondary">
              - {selectedUser.full_name}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, paddingTop: "24px !important", bgcolor: "#f8f9fa" }}
      >
        <DateFilters
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearAllFilters}
        />

        <ActiveFilters
          appliedStartDate={appliedStartDate}
          appliedEndDate={appliedEndDate}
          onClearStartDate={handleClearStartDate}
          onClearEndDate={handleClearEndDate}
        />

        {/* {breakData.length > 0 && <TimeFormatInfo />} */}

        {loading ? (
          <LoadingState userName={selectedUser?.full_name} />
        ) : breakData.length > 0 ? (
          <>
            <BreakLogList
              breakData={breakData}
              expandedBreak={expandedBreak}
              onExpandClick={handleExpandClick}
              detailsPageMap={detailsPageMap}
              detailsPerPage={detailsPerPage}
              onDetailsPageChange={handleDetailsPageChange}
            />

            <PaginationControls
              page={page}
              pagination={pagination}
              totalRecords={totalRecords}
              onPageChange={handlePageChange}
              onPaginationChange={handlePaginationChange}
            />
          </>
        ) : (
          <EmptyState userName={selectedUser?.full_name} />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{ px: 4, borderRadius: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BreakReportDialog;
